import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Plus, Pencil, Trash2, X, Save } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminServicing = () => {
  const { getAuthHeader } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ name: '', price: '', description: '', category: 'habitation' });

  const categories = [
    { value: 'habitation', label: 'Habitation Servicing' },
    { value: 'gaslow', label: 'Gaslow LPG' },
    { value: 'avtex', label: 'Avtex TV' },
    { value: 'other', label: 'Other Services' }
  ];

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/servicing`, getAuthHeader());
      setItems(response.data);
    } catch (error) {
      console.error('Error fetching servicing items:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.put(`${API_URL}/api/admin/servicing/${editingId}`, formData, getAuthHeader());
      } else {
        await axios.post(`${API_URL}/api/admin/servicing`, formData, getAuthHeader());
      }
      fetchItems();
      resetForm();
    } catch (error) {
      console.error('Error saving servicing item:', error);
    }
  };

  const handleEdit = (item) => {
    setFormData(item);
    setEditingId(item.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/servicing/${id}`, getAuthHeader());
      fetchItems();
    } catch (error) {
      console.error('Error deleting servicing item:', error);
    }
  };

  const resetForm = () => {
    setFormData({ name: '', price: '', description: '', category: 'habitation' });
    setEditingId(null);
    setShowForm(false);
  };

  const groupedItems = categories.map(cat => ({
    ...cat,
    items: items.filter(item => item.category === cat.value)
  })).filter(cat => cat.items.length > 0);

  if (loading) {
    return <div className="flex justify-center p-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Servicing</h1>
          <p className="text-slate-600">Manage servicing prices and items</p>
        </div>
        <Button onClick={() => setShowForm(true)}>
          <Plus className="h-4 w-4 mr-2" /> Add Item
        </Button>
      </div>

      {showForm && (
        <Card className="mb-6">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>{editingId ? 'Edit Item' : 'Add New Item'}</CardTitle>
            <Button variant="ghost" size="sm" onClick={resetForm}><X className="h-4 w-4" /></Button>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label>Service Name</Label>
                  <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required />
                </div>
                <div>
                  <Label>Price</Label>
                  <Input value={formData.price} onChange={(e) => setFormData({...formData, price: e.target.value})} placeholder="£175" required />
                </div>
                <div>
                  <Label>Category</Label>
                  <select className="w-full border rounded-md p-2" value={formData.category} onChange={(e) => setFormData({...formData, category: e.target.value})}>
                    {categories.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <Label>Description (optional)</Label>
                <Textarea value={formData.description || ''} onChange={(e) => setFormData({...formData, description: e.target.value})} rows={2} />
              </div>
              <div className="flex gap-2">
                <Button type="submit"><Save className="h-4 w-4 mr-2" /> Save Item</Button>
                <Button type="button" variant="outline" onClick={resetForm}>Cancel</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <div className="space-y-6">
        {groupedItems.map((group) => (
          <div key={group.value}>
            <h2 className="text-lg font-semibold text-slate-800 mb-3">{group.label}</h2>
            <div className="grid gap-3">
              {group.items.map((item) => (
                <Card key={item.id}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <h3 className="font-medium">{item.name}</h3>
                          <span className="text-lg font-bold text-blue-600">{item.price}</span>
                        </div>
                        {item.description && <p className="text-slate-600 text-sm mt-1">{item.description}</p>}
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => handleEdit(item)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="outline" size="sm" className="text-red-600" onClick={() => handleDelete(item.id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        ))}

        {items.length === 0 && (
          <Card>
            <CardContent className="p-8 text-center text-slate-500">
              No servicing items yet. Click "Add Item" to create one.
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AdminServicing;
